import { getWeekDayStrings } from './kpiTrackerUtils';

function addDaysToDateString(isoDate, days) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

export const ROOM_TYPES = ['RB', 'EX', 'SK'];
export const ROOM_COLORS = { RB: '#22C55E', EX: '#F59E0B', SK: '#EF4444' };

const ANSWERED_OUTCOMES = new Set(['answered', 'booked_meeting', 'closed_business']);
const CLOSED_OUTCOMES = new Set(['closed_business', 'booked_meeting']);
const TRANSIENT_OUTCOMES = new Set(['ringing', 'connecting']);

const STATUS_RANK = {
  new: 0,
  contacted: 1,
  follow_up: 2,
  appointment_set: 3,
  closed_won: 4,
  closed_lost: 0,
  dnc: -1,
};

export const DEFAULT_GOALS = {
  talk_time_target_seconds: 120,
  first_call_close_target_pct: 60,
  callback_show_target_pct: 40,
  skeptic_save_target_pct: 35,
  rb_conversion_target_pct: 60,
  ex_reengage_target_pct: 40,
};

function isRealCall(row) {
  return row.outcome && !TRANSIENT_OUTCOMES.has(row.outcome);
}

function pct(num, den) {
  if (!den) return 0;
  return Math.round((num / den) * 1000) / 10;
}

function avg(nums) {
  if (!nums.length) return 0;
  return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
}

function groupCallsByLead(calls) {
  const byLead = new Map();
  for (const c of calls) {
    if (!c.lead_id) continue;
    if (!byLead.has(c.lead_id)) byLead.set(c.lead_id, []);
    byLead.get(c.lead_id).push(c);
  }
  for (const list of byLead.values()) {
    list.sort((a, b) => new Date(a.called_at) - new Date(b.called_at));
  }
  return byLead;
}

function callsInRange(calls, startIso, endIso) {
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  return calls.filter((c) => {
    const t = new Date(c.called_at).getTime();
    return t >= start && t <= end && isRealCall(c);
  });
}

function callsOnDay(calls, dayKey) {
  return calls.filter((c) => {
    const d = new Date(c.called_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return key === dayKey && isRealCall(c);
  });
}

export function mergeGoals(userRow, agencyRow) {
  return { ...DEFAULT_GOALS, ...agencyRow, ...userRow };
}

export function formatTalkTime(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m === 0) return `${rem}s`;
  return `${m}m ${rem}s`;
}

export function targetBadgeStatus(actual, target, { higherIsBetter = true, isSeconds = false } = {}) {
  if (target == null || target === 0) return 'neutral';
  const a = Number(actual) || 0;
  const t = Number(target) || 0;
  if (isSeconds) {
    if (a >= t) return 'good';
    if (a >= t * 0.9) return 'warn';
    return 'bad';
  }
  if (higherIsBetter) {
    if (a >= t) return 'good';
    if (a >= t * 0.9) return 'warn';
    return 'bad';
  }
  if (a <= t) return 'good';
  if (a <= t * 1.1) return 'warn';
  return 'bad';
}

function computeMetricsForCalls(weekCalls, allCallsByLead, leadStatusById, weekStart, weekEnd) {
  const answered = weekCalls.filter((c) => ANSWERED_OUTCOMES.has(c.outcome));
  const talkTimeAvg = avg(
    answered.map((c) => Number(c.duration_seconds) || 0).filter((d) => d > 0),
  );

  let rbFirstCloseNum = 0;
  let rbFirstCloseDen = 0;
  let rbConversionNum = 0;
  let rbConversionDen = 0;
  let exCallbackNum = 0;
  let exCallbackDen = 0;
  let exReengageNum = 0;
  let exReengageDen = 0;
  let skSaveNum = 0;
  let skSaveDen = 0;

  const weekStartMs = new Date(weekStart).getTime();
  const weekEndMs = new Date(weekEnd).getTime();

  const rbLeadsCalled = new Set();
  const exLeadsCalled = new Set();

  for (const c of weekCalls) {
    if (c.room_type === 'RB') rbLeadsCalled.add(c.lead_id);
    if (c.room_type === 'EX') exLeadsCalled.add(c.lead_id);
    if (c.room_type === 'SK') {
      skSaveDen += 1;
      if ((Number(c.duration_seconds) || 0) >= 60) skSaveNum += 1;
    }
  }

  for (const leadId of rbLeadsCalled) {
    if (!leadId) continue;
    const history = allCallsByLead.get(leadId) || [];
    const rbCalls = history.filter((c) => c.room_type === 'RB');
    if (!rbCalls.length) continue;
    rbConversionDen += 1;
    const won = rbCalls.some((c) => CLOSED_OUTCOMES.has(c.outcome));
    if (won) rbConversionNum += 1;

    const first = rbCalls[0];
    const firstTime = new Date(first.called_at).getTime();
    if (firstTime >= weekStartMs && firstTime <= weekEndMs) {
      rbFirstCloseDen += 1;
      if (CLOSED_OUTCOMES.has(first.outcome)) rbFirstCloseNum += 1;
    }
  }

  for (const leadId of exLeadsCalled) {
    if (!leadId) continue;
    const history = (allCallsByLead.get(leadId) || []).filter((c) => c.room_type === 'EX');
    const weekEx = history.filter((c) => {
      const t = new Date(c.called_at).getTime();
      return t >= weekStartMs && t <= weekEndMs;
    });
    if (!weekEx.length) continue;
    exCallbackDen += 1;
    if (weekEx.length >= 2 || history.length >= 2) exCallbackNum += 1;

    exReengageDen += 1;
    const statusBefore = leadStatusById.get(leadId)?.statusBeforeWeek;
    const statusNow = leadStatusById.get(leadId)?.statusNow;
    const rankBefore = STATUS_RANK[statusBefore] ?? 0;
    const rankNow = STATUS_RANK[statusNow] ?? 0;
    if (history.length >= 2 && rankNow > rankBefore) exReengageNum += 1;
  }

  return {
    talkTimeAvg,
    firstCallClosePct: pct(rbFirstCloseNum, rbFirstCloseDen),
    rbConversionPct: pct(rbConversionNum, rbConversionDen),
    exReengagePct: pct(exReengageNum, exReengageDen),
    callbackShowPct: pct(exCallbackNum, exCallbackDen),
    skepticSavePct: pct(skSaveNum, skSaveDen),
    rbConversionNum,
    rbConversionDen,
    exReengageNum,
    exReengageDen,
    skSaveNum,
    skSaveDen,
  };
}

export function computeWeekStats(allCalls, leadMeta, weekStartDate) {
  const weekEnd = addDaysToDateString(weekStartDate, 6);
  const weekStartIso = dateStringToLocalStart(weekStartDate);
  const weekEndIso = dateStringToLocalEnd(weekEnd);
  const weekFiltered = callsInRange(allCalls, weekStartIso, weekEndIso);
  const allByLead = groupCallsByLead(allCalls);

  const weekly = computeMetricsForCalls(
    weekFiltered,
    allByLead,
    leadMeta,
    weekStartIso,
    weekEndIso,
  );

  const dayStrings = getWeekDayStrings(weekStartDate);
  const byDay = {};
  for (const day of dayStrings) {
    const dayCalls = callsOnDay(weekFiltered, day);
    byDay[day] = computeMetricsForCalls(
      dayCalls,
      allByLead,
      leadMeta,
      dateStringToLocalStart(day),
      dateStringToLocalEnd(day),
    );
  }

  return { byDay, weekly, weekStartDate, weekEndDate: weekEnd };
}

export function computeTypeDistribution(allCalls, weekStartDate) {
  const weekEnd = addDaysToDateString(weekStartDate, 6);
  const filtered = callsInRange(
    allCalls,
    dateStringToLocalStart(weekStartDate),
    dateStringToLocalEnd(weekEnd),
  );
  const counts = { RB: 0, EX: 0, SK: 0, other: 0 };
  for (const c of filtered) {
    if (ROOM_TYPES.includes(c.room_type)) counts[c.room_type] += 1;
    else counts.other += 1;
  }
  const total = counts.RB + counts.EX + counts.SK;
  const pctOf = (n) => (total ? Math.round((n / total) * 1000) / 10 : 0);
  return {
    counts: { RB: counts.RB, EX: counts.EX, SK: counts.SK },
    total,
    pct: { RB: pctOf(counts.RB), EX: pctOf(counts.EX), SK: pctOf(counts.SK) },
  };
}

export function computeFourWeekTrend(allCalls, weekStartDate) {
  const weeks = [];
  for (let i = 3; i >= 0; i -= 1) {
    const ws = addDaysToDateString(weekStartDate, -7 * i);
    const dist = computeTypeDistribution(allCalls, ws);
    weeks.push({
      weekStart: ws,
      RB: dist.counts.RB,
      EX: dist.counts.EX,
      SK: dist.counts.SK,
    });
  }
  return weeks;
}

export function computeMetricSparklines(allCalls, allCallsByLead, leadMeta, currentWeekStart) {
  const keys = [
    'talkTimeAvg',
    'firstCallClosePct',
    'callbackShowPct',
    'skepticSavePct',
    'exReengagePct',
    'rbConversionPct',
  ];
  const trends = {};
  for (const k of keys) trends[k] = [];

  for (let i = 3; i >= 0; i -= 1) {
    const ws = addDaysToDateString(currentWeekStart, -7 * i);
    const stats = computeWeekStats(allCalls, leadMeta, ws);
    for (const k of keys) trends[k].push(stats.weekly[k] ?? 0);
  }
  return trends;
}

export function computeBestCallTimes(weekCalls) {
  const hours = [];
  for (let h = 6; h <= 21; h += 1) hours.push(h);

  const grid = {};
  for (const rt of ROOM_TYPES) {
    grid[rt] = {};
    for (const h of hours) {
      grid[rt][h] = { calls: 0, closed: 0 };
    }
  }

  for (const c of weekCalls) {
    if (!ROOM_TYPES.includes(c.room_type) || !isRealCall(c)) continue;
    const h = new Date(c.called_at).getHours();
    if (h < 6 || h > 21) continue;
    grid[c.room_type][h].calls += 1;
    if (CLOSED_OUTCOMES.has(c.outcome)) grid[c.room_type][h].closed += 1;
  }

  const bestHourByType = {};
  const rows = [];
  for (const rt of ROOM_TYPES) {
    let bestH = null;
    let bestRate = -1;
    for (const h of hours) {
      const { calls, closed } = grid[rt][h];
      const rate = calls ? closed / calls : 0;
      rows.push({
        roomType: rt,
        hour: h,
        calls,
        closed,
        rate: calls ? Math.round(rate * 1000) / 10 : null,
      });
      if (calls >= 2 && rate > bestRate) {
        bestRate = rate;
        bestH = h;
      }
    }
    bestHourByType[rt] = bestH;
  }

  return { rows, bestHourByType, hours };
}

export function computeCoachingFlags(metrics, weekCalls, allCallsByLead, weekStartDate) {
  const flags = [];
  const weekEnd = addDaysToDateString(weekStartDate, 6);
  const weekStartIso = dateStringToLocalStart(weekStartDate);
  const weekEndIso = dateStringToLocalEnd(weekEnd);
  const weekFiltered = callsInRange(weekCalls, weekStartIso, weekEndIso);

  const skCalls = weekFiltered.filter((c) => c.room_type === 'SK');
  if (skCalls.length > 0) {
    const short = skCalls.filter((c) => (Number(c.duration_seconds) || 0) < 60).length;
    if (short / skCalls.length > 0.95) {
      flags.push({
        type: 'warning',
        message:
          'Less than 5% of Skeptic calls lasted 60+ seconds — objection handling needs work',
      });
    }
  }

  const exLeadsWeek = new Set(
    weekFiltered.filter((c) => c.room_type === 'EX').map((c) => c.lead_id),
  );
  let exWithSecond = 0;
  for (const leadId of exLeadsWeek) {
    if (!leadId) continue;
    const exHistory = (allCallsByLead.get(leadId) || []).filter((c) => c.room_type === 'EX');
    if (exHistory.length >= 2) exWithSecond += 1;
  }
  if (exLeadsWeek.size > 0 && exWithSecond === 0) {
    flags.push({
      type: 'warning',
      message: 'No Explorer leads were called back this week — follow-up discipline issue',
    });
  }

  if (metrics.rbConversionPct < 20 && metrics.rbConversionDen > 0) {
    flags.push({
      type: 'warning',
      message: 'Ready Buyer conversion below 20% — closing skills need coaching',
    });
  }

  if (metrics.firstCallClosePct < 30 && metrics.rbConversionDen > 0) {
    flags.push({
      type: 'warning',
      message: 'First-call close rate is low — review your RB qualification criteria',
    });
  }

  if (metrics.firstCallClosePct > 60) {
    flags.push({
      type: 'success',
      message: 'Strong first-call close rate this week',
    });
  }

  if (metrics.skepticSavePct > 35) {
    flags.push({
      type: 'success',
      message: 'Excellent Skeptic engagement this week',
    });
  }

  if (metrics.callbackShowPct > 40) {
    flags.push({
      type: 'success',
      message: 'Strong Explorer follow-up rate this week',
    });
  }

  return flags;
}

function dateStringToLocalStart(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

function dateStringToLocalEnd(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}
